import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { authService } from '@/services';
import { ApiError, User, LoginRequest } from '@/types/api';
import { getAuthToken, removeAuthToken } from '@/utils/fetchWrapper';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const APP_MFA_METHOD = 'app';

function extractSecretFromOtpauthUrl(otpauthUrl: string): string {
  try {
    const parsed = new URL(otpauthUrl);
    return parsed.searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

export function useAuth() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: !isLoginPage,
  });
  const checkingAuthRef = useRef(false);
  const hasCheckedAuthRef = useRef(isLoginPage);

  const finalizeAuthenticatedSession = useCallback(async () => {
    const user = await authService.getCurrentUser();
    setAuthState({
      user,
      isAuthenticated: true,
      isLoading: false,
    });
    hasCheckedAuthRef.current = true;
  }, []);

  const runAuthActionThenFinalize = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await action();
        await finalizeAuthenticatedSession();
      } catch (error) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        throw error;
      }
    },
    [finalizeAuthenticatedSession]
  );

  const login = useCallback(
    async (
      credentials: LoginRequest
    ): Promise<{
      requiresTwoFactor: boolean;
      requiresTwoFactorSetup?: boolean;
      ephemeralToken?: string;
      enrollmentToken?: string;
      otpauthUrl?: string;
      secretKey?: string;
      message?: string;
    }> => {
      try {
        const response = await authService.login(credentials);

        if (
          ('requires_2fa' in response && response.requires_2fa) ||
          ('mfa_enabled' in response &&
            response.mfa_enabled === true &&
            'ephemeral_token' in response)
        ) {
          return { requiresTwoFactor: true, ephemeralToken: response.ephemeral_token };
        }

        if ('requires_2fa_setup' in response) {
          return {
            requiresTwoFactor: false,
            requiresTwoFactorSetup: true,
            enrollmentToken: response.enrollment_token,
            otpauthUrl: response.otpauth_url,
            secretKey: response.secret_key,
            message: response.message,
          };
        }

        let mfaMethods;
        try {
          mfaMethods = await authService.getMfaMethods();
        } catch (error) {
          const apiError = error as ApiError;
          if (apiError.status === 404) {
            await finalizeAuthenticatedSession();
            return { requiresTwoFactor: false };
          }
          throw error;
        }
        const appMethod = mfaMethods.find(method => method.name === APP_MFA_METHOD);
        if (!appMethod?.is_setup) {
          const setupResponse = await authService.createMfaMethod(APP_MFA_METHOD);
          const otpauthUrl = setupResponse.setup_data.qr_link;
          return {
            requiresTwoFactor: false,
            requiresTwoFactorSetup: true,
            otpauthUrl,
            secretKey: extractSecretFromOtpauthUrl(otpauthUrl),
          };
        }

        if (!appMethod.is_active) {
          throw new Error(
            'Two-factor authentication setup is incomplete. Please reset MFA and set it up again.'
          );
        }

        await finalizeAuthenticatedSession();
        return { requiresTwoFactor: false };
      } catch (error) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        throw error;
      }
    },
    [finalizeAuthenticatedSession]
  );

  const verify2fa = useCallback(
    async (
      ephemeralToken: string,
      otpToken: string,
      rememberMe: boolean = false
    ): Promise<void> => {
      await runAuthActionThenFinalize(() =>
        authService.verify2fa({
          ephemeral_token: ephemeralToken,
          code: otpToken,
          remember_me: rememberMe,
        })
      );
    },
    [runAuthActionThenFinalize]
  );

  const setup2fa = useCallback(
    async (otpToken: string): Promise<void> => {
      await runAuthActionThenFinalize(async () => {
        await authService.confirmMfaMethod({
          method: APP_MFA_METHOD,
          code: otpToken,
        });
      });
    },
    [runAuthActionThenFinalize]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Continue — local state is cleared below regardless.
    } finally {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      hasCheckedAuthRef.current = false;
      checkingAuthRef.current = false;

      // Hard redirect ensures cookies and in-memory auth state are fully reset.
      window.location.href = '/login';
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (checkingAuthRef.current) {
      return;
    }

    const currentPath = window.location.pathname;
    if (currentPath === '/login') {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      hasCheckedAuthRef.current = true;
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      hasCheckedAuthRef.current = true;
      return;
    }

    checkingAuthRef.current = true;

    try {
      const user = await authService.getCurrentUser();
      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      hasCheckedAuthRef.current = true;
    } catch {
      removeAuthToken();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      hasCheckedAuthRef.current = true;
    } finally {
      checkingAuthRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/login') {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      hasCheckedAuthRef.current = true;
      checkingAuthRef.current = false;
      return;
    }

    if (!hasCheckedAuthRef.current && !checkingAuthRef.current) {
      checkAuth();
    }
  }, [location.pathname, checkAuth]);

  return {
    ...authState,
    login,
    logout,
    verify2fa,
    setup2fa,
    checkAuth,
  };
}
